import {
  ArrowDownIcon,
  ArrowPathIcon,
  ArrowTrendingUpIcon,
  ArrowUpIcon,
  ArrowsPointingOutIcon,
  ChartBarIcon,
  CheckCircleIcon,
  ClockIcon,
  CurrencyDollarIcon,
  DocumentArrowDownIcon,
  ExclamationTriangleIcon,
  EyeIcon,
  LightBulbIcon,
} from '@heroicons/react/24/outline';
import React, { useState } from 'react';
import { toast } from 'react-hot-toast';
import DateRangeFilter from './DateRangeFilter';
import ExportStockingGuideModal from './ExportStockingGuideModal';
import StockingGuideGrid from './StockingGuideGrid';

interface StockingGuideDashboardProps {
  data: {
    summary: any;
    recommendations: any[];
    turnoverAnalysis: any[];
    profitOptimizations: any[];
    demandForecasts: any[];
  };
  loading: boolean;
  onRefresh: () => void;
  dateRange: { startDate: string | null; endDate: string | null };
  onDateRangeChange: (range: { startDate: string | null; endDate: string | null }) => void;
  lastUpdated?: Date | null;
  refreshInterval?: number;
  onRefreshIntervalChange?: (interval: number) => void;
  isRefetching?: boolean;
}

const StockingGuideDashboard: React.FC<StockingGuideDashboardProps> = ({
  data,
  loading,
  onRefresh,
  dateRange,
  onDateRangeChange,
  lastUpdated,
  refreshInterval = 0,
  onRefreshIntervalChange,
  isRefetching = false,
}) => {
  const [activeSection, setActiveSection] = useState('overview');
  const [retryCount] = useState(0);
  const [error] = useState<string | null>(null);
  const [isRetrying] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [isEditingLayout, setIsEditingLayout] = useState(false);

  // Dashboard customization state
  const [dashboardSettings, setDashboardSettings] = useState({
    showMarketTrends: true,
    showPricePredictions: true,
    showDemandForecast: true,
    showSeasonalData: true,
    alertPreferences: {
      emailNotifications: true,
      browserNotifications: false,
      auctionAlerts: true,
      priceAlerts: true,
    },
    layoutPreferences: {
      compactView: false,
      showCharts: true,
      autoRefresh: true,
      refreshInterval: 300, // 5 minutes
    },
  });
  const [showSettings, setShowSettings] = useState(false);

  const formatCurrency = (amount?: number) => {
    if (!amount) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0,
    }).format(amount);
  };

  // Responsive class helpers based on dashboard settings
  const getCardPadding = () => (dashboardSettings.layoutPreferences.compactView ? 'p-2' : 'p-4');
  const getLargeCardPadding = () =>
    dashboardSettings.layoutPreferences.compactView ? 'p-3' : 'p-6';
  const getTextSize = () =>
    dashboardSettings.layoutPreferences.compactView ? 'text-lg' : 'text-2xl';
  const getIconSize = () =>
    dashboardSettings.layoutPreferences.compactView ? 'w-6 h-6' : 'w-8 h-8';
  const getSmallIconSize = () =>
    dashboardSettings.layoutPreferences.compactView ? 'w-4 h-4' : 'w-5 h-5';

  const formatPercentage = (value?: number) => {
    if (!value) return 'N/A';
    return `${(value * 100).toFixed(1)}%`;
  };

  const mockRecommendations = [
    {
      recommendation_type: 'increase_stock',
      vehicle_make: 'Toyota',
      vehicle_model: 'Camry',
      priority_level: 'high',
      reasoning: 'High demand model with low local inventory coverage.',
      recommended_quantity: 3,
      priority_score: 8.6,
      market_demand_score: 0.78,
      implementation_timeline: '1-2 weeks',
    },
    {
      recommendation_type: 'maintain_stock',
      vehicle_make: 'Honda',
      vehicle_model: 'Civic',
      priority_level: 'medium',
      reasoning: 'Stable demand with healthy turnover in the last 30 days.',
      recommended_quantity: 2,
      priority_score: 6.4,
      market_demand_score: 0.62,
      implementation_timeline: '2-3 weeks',
    },
    {
      recommendation_type: 'reduce_stock',
      vehicle_make: 'Nissan',
      vehicle_model: 'Altima',
      priority_level: 'low',
      reasoning: 'Oversupply in local market and slower turn rates.',
      recommended_quantity: 1,
      priority_score: 4.1,
      market_demand_score: 0.38,
      implementation_timeline: '30-45 days',
    },
  ];

  const mockTurnoverAnalysis = [
    {
      analysis_period: '30 days',
      analysis_date: new Date().toISOString(),
      inventory_turnover_rate: 2.4,
      average_turnover_days: 38,
      total_vehicles_sold: 22,
      average_inventory_value: 18450,
      insights: {
        recommendations: [
          'Accelerate pricing on slow-moving sedans to improve turn.',
          'Increase acquisition of compact SUVs for higher velocity.',
        ],
      },
    },
    {
      analysis_period: '90 days',
      analysis_date: new Date().toISOString(),
      inventory_turnover_rate: 2.1,
      average_turnover_days: 44,
      total_vehicles_sold: 64,
      average_inventory_value: 19200,
      insights: {
        recommendations: [
          'Reduce exposure to low-demand trims in full-size trucks.',
          'Rebalance inventory toward higher margin compact SUVs.',
        ],
      },
    },
  ];

  const mockProfitOptimizations = [
    {
      vehicle_make: 'Ford',
      vehicle_model: 'F-150',
      implementation_priority: 'high',
      current_price: 24900,
      optimal_price: 26250,
      expected_profit_increase: 1350,
      confidence_score: 82,
      recommendation_text:
        'Market comps indicate room for a 5% price increase without impacting velocity.',
    },
    {
      vehicle_make: 'Hyundai',
      vehicle_model: 'Tucson',
      implementation_priority: 'medium',
      current_price: 19900,
      optimal_price: 20750,
      expected_profit_increase: 850,
      confidence_score: 74,
      recommendation_text: 'Seasonal demand uptick suggests modest margin expansion.',
    },
  ];

  const mockDemandForecasts = [
    {
      forecast_period: 'Next 30 days',
      season_type: 'Late Winter',
      confidence_score: 79,
      forecasted_demand: 18,
      confidence_interval_lower: 14,
      confidence_interval_upper: 22,
      trend_direction: 'INCREASING',
      reorder_point: 6,
      stocking_recommendation:
        'Increase compact SUV inventory by 15% to stay ahead of seasonal demand.',
    },
    {
      forecast_period: 'Next 90 days',
      season_type: 'Spring',
      confidence_score: 72,
      forecasted_demand: 42,
      confidence_interval_lower: 36,
      confidence_interval_upper: 50,
      trend_direction: 'STABLE',
      reorder_point: 10,
      stocking_recommendation:
        'Maintain sedan mix while reallocating floorplan to fast-turning trims.',
    },
  ];

  const isRecommendationsMock = !data.recommendations || data.recommendations.length === 0;
  const isTurnoverMock = !data.turnoverAnalysis || data.turnoverAnalysis.length === 0;
  const isOptimizationMock = !data.profitOptimizations || data.profitOptimizations.length === 0;
  const isForecastMock = !data.demandForecasts || data.demandForecasts.length === 0;
  const isAuctionsMock = true;
  const isAnyMock = isRecommendationsMock || isTurnoverMock || isOptimizationMock || isForecastMock;
  const mockSectionLabels = [
    isRecommendationsMock ? 'Recommendations' : null,
    isTurnoverMock ? 'Turnover Analysis' : null,
    isOptimizationMock ? 'Profit Optimization' : null,
    isForecastMock ? 'Demand Forecasting' : null,
  ]
    .filter(Boolean)
    .join(', ');

  const recommendationData = isRecommendationsMock ? mockRecommendations : data.recommendations;
  const turnoverData = isTurnoverMock ? mockTurnoverAnalysis : data.turnoverAnalysis;
  const optimizationData = isOptimizationMock ? mockProfitOptimizations : data.profitOptimizations;
  const forecastData = isForecastMock ? mockDemandForecasts : data.demandForecasts;

  const handleSectionRefresh = (message: string) => {
    onRefresh();
    toast.success(message);
  };

  const getPriorityColor = (priority: string) => {
    switch (priority?.toLowerCase()) {
      case 'high':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'low':
        return 'bg-green-100 text-green-800 border-green-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getRecommendationTypeIcon = (type: string) => {
    switch (type?.toLowerCase()) {
      case 'increase_stock':
        return <ArrowUpIcon className="w-4 h-4 text-green-500" />;
      case 'reduce_stock':
        return <ArrowDownIcon className="w-4 h-4 text-red-500" />;
      case 'maintain_stock':
        return <CheckCircleIcon className="w-4 h-4 text-blue-500" />;
      default:
        return <LightBulbIcon className="w-4 h-4 text-gray-500" />;
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        {/* Header skeleton */}
        <div className="flex items-center justify-between">
          <div className="animate-pulse">
            <div className="h-6 bg-gray-200 rounded w-48 mb-2"></div>
            <div className="h-4 bg-gray-200 rounded w-64"></div>
          </div>
          <div className="h-10 bg-gray-200 rounded w-24 animate-pulse"></div>
        </div>

        {/* Navigation skeleton */}
        <div className="border-b border-gray-200">
          <div className="flex space-x-8 -mb-px">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="py-2 px-1 animate-pulse">
                <div className="h-4 bg-gray-200 rounded w-16"></div>
              </div>
            ))}
          </div>
        </div>

        {/* Content skeleton */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-lg border border-gray-200 p-6 animate-pulse">
            <div className="h-5 bg-gray-200 rounded w-32 mb-4"></div>
            <div className="space-y-3">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="flex justify-between items-center">
                  <div className="h-4 bg-gray-200 rounded w-24"></div>
                  <div className="h-4 bg-gray-200 rounded w-8"></div>
                </div>
              ))}
            </div>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-6 animate-pulse">
            <div className="h-5 bg-gray-200 rounded w-40 mb-4"></div>
            <div className="space-y-3">
              {[...Array(3)].map((_, i) => (
                <div
                  key={i}
                  className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg animate-pulse"
                >
                  <div className="w-4 h-4 bg-gray-200 rounded"></div>
                  <div className="flex-1">
                    <div className="h-4 bg-gray-200 rounded w-32 mb-2"></div>
                    <div className="h-3 bg-gray-200 rounded w-48"></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Controls row - high-density single-line actions */}
      <div className="flex flex-row items-center justify-between gap-2 md:gap-4 bg-white dark:bg-gray-800 p-2 md:p-4 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm relative z-40">
        <div className="flex flex-nowrap gap-2 items-center flex-shrink-1">
          {error && (
            <div className="p-2 bg-red-50 border border-red-200 rounded text-sm text-red-700 whitespace-nowrap">
              {error}
              {retryCount > 0 && (
                <span className="ml-2 text-xs text-red-600">(Retry: {retryCount})</span>
              )}
            </div>
          )}

          <div className="flex-shrink-0">
            <DateRangeFilter dateRange={dateRange} onFilterChange={onDateRangeChange} />
          </div>

          <div className="h-6 w-px bg-gray-200 hidden sm:block mx-1"></div>

          {onRefreshIntervalChange && (
            <div className="relative flex-shrink-0 hidden sm:block">
              <select
                value={refreshInterval}
                onChange={(e) => onRefreshIntervalChange(Number(e.target.value))}
                className="btn-secondary text-sm py-2 pl-2 pr-8 h-9 border-gray-300 focus:border-blue-500 focus:ring-blue-500 rounded-md shadow-sm"
                title="Auto-refresh interval"
                disabled={loading}
              >
                <option value={0}>Auto: Off</option>
                <option value={300000}>5 min</option>
                <option value={900000}>15 min</option>
              </select>
            </div>
          )}
        </div>

        <div className="flex flex-nowrap whitespace-nowrap gap-2 items-center ml-auto">
          {lastUpdated && (
            <span
              className="text-xs text-gray-400 hidden 2xl:inline-block mr-2"
              title={lastUpdated.toLocaleString()}
            >
              {lastUpdated.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </span>
          )}

          <div className="flex items-center gap-1.5 p-1 bg-gray-50 dark:bg-gray-900/50 rounded-lg">
            <button
              onClick={() => setShowExportModal(true)}
              className="p-2 hover:bg-white dark:hover:bg-gray-800 text-gray-600 dark:text-gray-400 rounded-md transition-all flex items-center gap-2 border border-transparent hover:border-gray-200 hover:shadow-sm"
              title="Export Report"
            >
              <DocumentArrowDownIcon className="h-5 w-5" />
              <span className="text-xs font-semibold hidden md:inline">Export</span>
            </button>

            {activeSection === 'overview' && (
              <button
                onClick={() => setIsEditingLayout(!isEditingLayout)}
                className={`p-2 rounded-md transition-all flex items-center gap-2 border ${isEditingLayout ? 'bg-blue-600 text-white border-blue-600 shadow-md' : 'hover:bg-white dark:hover:bg-gray-800 text-gray-600 dark:text-gray-400 border-transparent hover:border-gray-200 hover:shadow-sm'}`}
                title="Reorder Widgets"
              >
                <ArrowsPointingOutIcon className="h-5 w-5" />
                <span className="text-xs font-semibold hidden md:inline">
                  {isEditingLayout ? 'Save' : 'Edit'}
                </span>
              </button>
            )}

            <button
              onClick={onRefresh}
              className="p-2 hover:bg-white dark:hover:bg-gray-800 text-gray-600 dark:text-gray-400 rounded-md transition-all flex items-center gap-2 border border-transparent hover:border-gray-200 hover:shadow-sm"
              disabled={loading || isRetrying || isRefetching}
              title="Sync Data"
            >
              <ArrowPathIcon
                className={`h-5 w-5 ${loading || isRetrying || isRefetching ? 'animate-spin' : ''}`}
              />
              <span className="text-xs font-semibold hidden md:inline">Refresh</span>
            </button>
          </div>

          <button
            onClick={() => setShowSettings(true)}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-500 rounded-lg transition-all"
            title="Dashboard Settings"
          >
            ⚙️
          </button>
        </div>
      </div>

      {isAnyMock && (
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 rounded-lg border border-yellow-200 bg-yellow-50 px-4 py-3 text-sm text-yellow-800">
          <div>
            <span className="font-semibold">Demo data active:</span> {mockSectionLabels}.
            <span className="ml-2 text-yellow-700">
              Connect live data to replace these sections.
            </span>
          </div>
          <button
            onClick={() => handleSectionRefresh('Refreshing live Stocking Guide data...')}
            className="btn-secondary text-xs"
          >
            Refresh Live Data
          </button>
        </div>
      )}

      {/* Navigation Tabs */}
      <div className="border-b border-gray-200 overflow-x-auto scrollbar-hide relative z-10">
        <nav className="-mb-px flex space-x-8">
          {[
            { id: 'overview', label: 'Overview', icon: ChartBarIcon },
            { id: 'recommendations', label: 'Recommendations', icon: LightBulbIcon },
            { id: 'turnover', label: 'Turnover Analysis', icon: ArrowTrendingUpIcon },
            { id: 'optimization', label: 'Profit Optimization', icon: CurrencyDollarIcon },
            { id: 'forecasting', label: 'Demand Forecasting', icon: ClockIcon },
            { id: 'auctions', label: 'Auction Alerts', icon: ExclamationTriangleIcon },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveSection(tab.id)}
              className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center ${
                activeSection === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <tab.icon className="w-4 h-4 mr-2" />
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Overview Section */}
      {activeSection === 'overview' && (
        <StockingGuideGrid isDraggable={isEditingLayout}>
          {/* Predictive Analytics Summary */}
          <div
            key="metric-1"
            className={`bg-white rounded-lg border border-gray-200 ${getCardPadding()} h-full`}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Market Trend</p>
                <p className={`${getTextSize()} font-bold text-green-600`}>↗️ +12%</p>
              </div>
              <ArrowTrendingUpIcon className={`${getIconSize()} text-green-500`} />
            </div>
            <p className="text-xs text-gray-500 mt-2">vs last month</p>
          </div>

          <div
            key="metric-2"
            className={`bg-white rounded-lg border border-gray-200 ${getCardPadding()} h-full`}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Price Prediction</p>
                <p className={`${getTextSize()} font-bold text-blue-600`}>$28.5K</p>
              </div>
              <CurrencyDollarIcon className={`${getIconSize()} text-blue-500`} />
            </div>
            <p className="text-xs text-gray-500 mt-2">avg next 30 days</p>
          </div>

          <div
            key="metric-3"
            className={`bg-white rounded-lg border border-gray-200 ${getCardPadding()} h-full`}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Demand Forecast</p>
                <p className={`${getTextSize()} font-bold text-purple-600`}>85%</p>
              </div>
              <ChartBarIcon className={`${getIconSize()} text-purple-500`} />
            </div>
            <p className="text-xs text-gray-500 mt-2">confidence score</p>
          </div>

          <div
            key="metric-4"
            className={`bg-white rounded-lg border border-gray-200 ${getCardPadding()} h-full`}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Seasonal Peak</p>
                <p className={`${getTextSize()} font-bold text-orange-600`}>Q4</p>
              </div>
              <ClockIcon className={`${getIconSize()} text-orange-500`} />
            </div>
            <p className="text-xs text-gray-500 mt-2">next 3 months</p>
          </div>

          {/* Enhanced Analytics Grid Items */}
          {dashboardSettings.layoutPreferences.showCharts && (
            <div
              key="analytics"
              className={`bg-white rounded-lg border border-gray-200 ${getLargeCardPadding()} h-full`}
            >
              <h4 className="text-md font-semibold text-gray-900 mb-4 flex items-center">
                <ChartBarIcon className={`${getSmallIconSize()} mr-2 text-blue-500`} />
                Analytics Summary
              </h4>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Stock Recommendations:</span>
                  <span className="font-medium">{recommendationData?.length || 0}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Turnover Analyses:</span>
                  <span className="font-medium">{turnoverData?.length || 0}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Profit Optimizations:</span>
                  <span className="font-medium">{optimizationData?.length || 0}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Demand Forecasts:</span>
                  <span className="font-medium">{forecastData?.length || 0}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Predictive Models:</span>
                  <span className="font-medium text-green-600">4 Active</span>
                </div>
              </div>
            </div>
          )}

          {/* High Priority Recommendations */}
          <div
            key="actions"
            className={`bg-white rounded-lg border border-gray-200 ${getLargeCardPadding()} h-full overflow-y-auto`}
          >
            <h4 className="text-md font-semibold text-gray-900 mb-4 flex items-center">
              <ExclamationTriangleIcon className={`${getSmallIconSize()} mr-2 text-red-500`} />
              High Priority Actions
            </h4>
            {recommendationData?.filter((r) => r.priority_level === 'high').length > 0 ? (
              <div className="space-y-3">
                {recommendationData
                  .filter((r) => r.priority_level === 'high')
                  .slice(0, 3)
                  .map((rec, index) => (
                    <div
                      key={index}
                      className="flex items-start space-x-3 p-3 bg-red-50 rounded-lg"
                    >
                      {getRecommendationTypeIcon(rec.recommendation_type)}
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-900">
                          {rec.vehicle_make} {rec.vehicle_model}
                        </p>
                        <p className="text-xs text-gray-600">{rec.reasoning}</p>
                        <div className="flex items-center mt-2 space-x-4">
                          <span className="text-xs text-gray-500">
                            Confidence: {rec.priority_score}/10
                          </span>
                          <span className="text-xs text-blue-600">
                            Market Demand: {formatPercentage(rec.market_demand_score)}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500">No high priority recommendations</p>
            )}
          </div>

          {/* Predictive Insights */}
          <div
            key="insights"
            className={`bg-white rounded-lg border border-gray-200 ${getLargeCardPadding()} h-full`}
          >
            <h4 className="text-md font-semibold text-gray-900 mb-4 flex items-center">
              <EyeIcon className={`${getSmallIconSize()} mr-2 text-purple-500`} />
              Predictive Insights
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div className="p-4 bg-green-50 rounded-lg">
                <div className="flex items-center mb-2">
                  <ArrowUpIcon className="w-4 h-4 text-green-600 mr-2" />
                  <span className="text-sm font-medium text-green-800">Bull Market Signal</span>
                </div>
                <p className="text-xs text-green-700">
                  Used car prices expected to rise 8-12% in next quarter based on economic
                  indicators.
                </p>
              </div>

              <div className="p-4 bg-blue-50 rounded-lg">
                <div className="flex items-center mb-2">
                  <ClockIcon className="w-4 h-4 text-blue-600 mr-2" />
                  <span className="text-sm font-medium text-blue-800">Seasonal Demand</span>
                </div>
                <p className="text-xs text-blue-700">
                  SUVs and trucks showing 25% higher demand in winter months. Stock accordingly.
                </p>
              </div>

              <div className="p-4 bg-orange-50 rounded-lg">
                <div className="flex items-center mb-2">
                  <CurrencyDollarIcon className="w-4 h-4 text-orange-600 mr-2" />
                  <span className="text-sm font-medium text-orange-800">Price Optimization</span>
                </div>
                <p className="text-xs text-orange-700">
                  15 vehicles identified for price increases with $45K potential profit uplift.
                </p>
              </div>
            </div>
          </div>
        </StockingGuideGrid>
      )}

      {/* Recommendations Section */}
      {activeSection === 'recommendations' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <h4 className="text-md font-semibold text-gray-900">Stock Recommendations</h4>
              {isRecommendationsMock && (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                  Demo data
                </span>
              )}
            </div>
            <button
              onClick={() => handleSectionRefresh('Refreshing recommendations...')}
              className="btn-secondary text-sm"
            >
              Generate New
            </button>
          </div>

          {recommendationData?.length > 0 ? (
            <div className="grid gap-4">
              {recommendationData.map((rec, index) => (
                <div
                  key={index}
                  className={`bg-white rounded-lg border border-gray-200 ${getCardPadding()}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-3">
                      {getRecommendationTypeIcon(rec.recommendation_type)}
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-2">
                          <h5 className="font-medium text-gray-900">
                            {rec.vehicle_make} {rec.vehicle_model}
                          </h5>
                          <span
                            className={`px-2 py-1 text-xs font-medium rounded-full border ${getPriorityColor(rec.priority_level)}`}
                          >
                            {rec.priority_level}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600 mb-3">{rec.reasoning}</p>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                          <div>
                            <span className="text-gray-500">Recommended Qty:</span>
                            <span className="font-medium ml-1">{rec.recommended_quantity}</span>
                          </div>
                          <div>
                            <span className="text-gray-500">Priority Score:</span>
                            <span className="font-medium ml-1">{rec.priority_score}/10</span>
                          </div>
                          <div>
                            <span className="text-gray-500">Market Demand:</span>
                            <span className="font-medium ml-1">
                              {formatPercentage(rec.market_demand_score)}
                            </span>
                          </div>
                          <div>
                            <span className="text-gray-500">Timeline:</span>
                            <span className="font-medium ml-1">{rec.implementation_timeline}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <LightBulbIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                No Recommendations Available
              </h3>
              <p className="text-gray-500 mb-4">
                Generate stock recommendations to optimize your inventory.
              </p>
              <button
                className="btn-primary"
                onClick={() => handleSectionRefresh('Generating recommendations...')}
              >
                Generate Recommendations
              </button>
            </div>
          )}
        </div>
      )}

      {/* Turnover Analysis Section */}
      {activeSection === 'turnover' && (
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <h4 className="text-md font-semibold text-gray-900">Inventory Turnover Analysis</h4>
            {isTurnoverMock && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                Demo data
              </span>
            )}
          </div>

          {turnoverData?.length > 0 ? (
            <div className="grid gap-4">
              {turnoverData.map((analysis, index) => (
                <div
                  key={index}
                  className={`bg-white rounded-lg border border-gray-200 ${getCardPadding()}`}
                >
                  <div className="flex items-center justify-between mb-4">
                    <h5 className="font-medium text-gray-900">
                      {analysis.analysis_period} Analysis
                    </h5>
                    <span className="text-sm text-gray-500">
                      {new Date(analysis.analysis_date).toLocaleDateString()}
                    </span>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                    <div className="text-center">
                      <div className={`${getTextSize()} font-bold text-blue-600`}>
                        {analysis.inventory_turnover_rate}
                      </div>
                      <div className="text-xs text-gray-500">Turnover Rate</div>
                    </div>
                    <div className="text-center">
                      <div className={`${getTextSize()} font-bold text-green-600`}>
                        {analysis.average_turnover_days}
                      </div>
                      <div className="text-xs text-gray-500">Avg Days</div>
                    </div>
                    <div className="text-center">
                      <div className={`${getTextSize()} font-bold text-purple-600`}>
                        {analysis.total_vehicles_sold}
                      </div>
                      <div className="text-xs text-gray-500">Vehicles Sold</div>
                    </div>
                    <div className="text-center">
                      <div className={`${getTextSize()} font-bold text-orange-600`}>
                        {formatCurrency(analysis.average_inventory_value)}
                      </div>
                      <div className="text-xs text-gray-500">Avg Value</div>
                    </div>
                  </div>

                  {analysis.insights && (
                    <div className="bg-gray-50 rounded-lg p-3">
                      <h6 className="text-sm font-medium text-gray-900 mb-2">Insights:</h6>
                      <ul className="text-xs text-gray-600 space-y-1">
                        {analysis.insights.recommendations?.map((rec: string, idx: number) => (
                          <li key={idx} className="flex items-start">
                            <span className="text-blue-500 mr-2">•</span>
                            {rec}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <ArrowTrendingUpIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                No Turnover Analysis Available
              </h3>
              <p className="text-gray-500 mb-4">
                Run turnover analysis to understand inventory performance.
              </p>
              <button
                className="btn-primary"
                onClick={() => handleSectionRefresh('Running turnover analysis...')}
              >
                Run Analysis
              </button>
            </div>
          )}
        </div>
      )}

      {/* Profit Optimization Section */}
      {activeSection === 'optimization' && (
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <h4 className="text-md font-semibold text-gray-900">Profit Optimization</h4>
            {isOptimizationMock && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                Demo data
              </span>
            )}
          </div>

          {optimizationData?.length > 0 ? (
            <div className="grid gap-4">
              {optimizationData.map((opt, index) => (
                <div
                  key={index}
                  className={`bg-white rounded-lg border border-gray-200 ${getCardPadding()}`}
                >
                  <div className="flex items-center justify-between mb-4">
                    <h5 className="font-medium text-gray-900">
                      {opt.vehicle_make} {opt.vehicle_model}
                    </h5>
                    <span
                      className={`px-2 py-1 text-xs font-medium rounded-full ${
                        opt.implementation_priority === 'high'
                          ? 'bg-red-100 text-red-800'
                          : opt.implementation_priority === 'medium'
                            ? 'bg-yellow-100 text-yellow-800'
                            : 'bg-green-100 text-green-800'
                      }`}
                    >
                      {opt.implementation_priority} priority
                    </span>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                    <div>
                      <div className="text-sm text-gray-500">Current Price</div>
                      <div className="font-medium">{formatCurrency(opt.current_price)}</div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-500">Optimal Price</div>
                      <div className="font-medium text-green-600">
                        {formatCurrency(opt.optimal_price)}
                      </div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-500">Potential Increase</div>
                      <div className="font-medium text-green-600">
                        {formatCurrency(opt.expected_profit_increase)}
                      </div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-500">Confidence</div>
                      <div className="font-medium">{opt.confidence_score}/100</div>
                    </div>
                  </div>

                  <p className="text-sm text-gray-600">{opt.recommendation_text}</p>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <CurrencyDollarIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                No Profit Optimization Available
              </h3>
              <p className="text-gray-500 mb-4">
                Run profit optimization analysis to maximize margins.
              </p>
              <button
                className="btn-primary"
                onClick={() => handleSectionRefresh('Running profit optimization...')}
              >
                Run Optimization
              </button>
            </div>
          )}
        </div>
      )}

      {/* Demand Forecasting Section */}
      {activeSection === 'forecasting' && (
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <h4 className="text-md font-semibold text-gray-900">Demand Forecasting</h4>
            {isForecastMock && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                Demo data
              </span>
            )}
          </div>

          {forecastData?.length > 0 ? (
            <div className="grid gap-4">
              {forecastData.map((forecast, index) => (
                <div
                  key={index}
                  className={`bg-white rounded-lg border border-gray-200 ${getCardPadding()}`}
                >
                  <div className="flex items-center justify-between mb-4">
                    <h5 className="font-medium text-gray-900">{forecast.forecast_period}</h5>
                    <span className="text-sm text-gray-500">
                      {forecast.season_type} • {forecast.confidence_score}/100 confidence
                    </span>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                    <div>
                      <div className="text-sm text-gray-500">Forecasted Demand</div>
                      <div className="font-medium">{forecast.forecasted_demand} units</div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-500">Confidence Range</div>
                      <div className="font-medium">
                        {forecast.confidence_interval_lower} - {forecast.confidence_interval_upper}
                      </div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-500">Trend</div>
                      <div
                        className={`font-medium ${
                          forecast.trend_direction === 'INCREASING'
                            ? 'text-green-600'
                            : forecast.trend_direction === 'DECREASING'
                              ? 'text-red-600'
                              : 'text-gray-600'
                        }`}
                      >
                        {forecast.trend_direction}
                      </div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-500">Reorder Point</div>
                      <div className="font-medium">{forecast.reorder_point} units</div>
                    </div>
                  </div>

                  <div className="bg-blue-50 rounded-lg p-3">
                    <p className="text-sm text-blue-800">{forecast.stocking_recommendation}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <ClockIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                No Demand Forecasts Available
              </h3>
              <p className="text-gray-500 mb-4">
                Generate demand forecasts to predict market needs.
              </p>
              <button
                className="btn-primary"
                onClick={() => handleSectionRefresh('Generating demand forecasts...')}
              >
                Generate Forecasts
              </button>
            </div>
          )}
        </div>
      )}

      {/* Auction Alerts Section */}
      {activeSection === 'auctions' && (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <h4 className="text-md font-semibold text-gray-900 flex items-center">
                <ExclamationTriangleIcon className="w-5 h-5 mr-2 text-red-500" />
                Real-Time Auction Alerts
              </h4>
              {isAuctionsMock && (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                  Demo data
                </span>
              )}
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => handleSectionRefresh('Refreshing auction data...')}
                className="btn-secondary text-sm"
              >
                <ArrowPathIcon className="h-4 w-4 mr-2" />
                Refresh
              </button>
              <button
                onClick={() => handleSectionRefresh('Saving auction alert preferences...')}
                className="btn-secondary text-sm"
              >
                Configure Alerts
              </button>
            </div>
          </div>

          {isAuctionsMock && (
            <div className="rounded-lg border border-yellow-200 bg-yellow-50 px-4 py-3 text-sm text-yellow-800">
              Demo auction alerts are shown until live auction feeds are connected.
            </div>
          )}

          {/* Alert Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-red-800">Hot Deals</p>
                  <p className="text-2xl font-bold text-red-600">3</p>
                </div>
                <ExclamationTriangleIcon className="w-8 h-8 text-red-500" />
              </div>
              <p className="text-xs text-red-700 mt-2">Below market value</p>
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-yellow-800">Watch List</p>
                  <p className="text-2xl font-bold text-yellow-600">7</p>
                </div>
                <EyeIcon className="w-8 h-8 text-yellow-500" />
              </div>
              <p className="text-xs text-yellow-700 mt-2">Close to target price</p>
            </div>

            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-green-800">Won Today</p>
                  <p className="text-2xl font-bold text-green-600">2</p>
                </div>
                <CheckCircleIcon className="w-8 h-8 text-green-500" />
              </div>
              <p className="text-xs text-green-700 mt-2">Successful acquisitions</p>
            </div>
          </div>

          {/* Active Alerts */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h5 className="text-md font-semibold text-gray-900 mb-4">Active Auction Alerts</h5>
            <div className="space-y-4">
              {/* Sample Alert 1 */}
              <div className="flex items-start space-x-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                <ExclamationTriangleIcon className="w-6 h-6 text-red-500 flex-shrink-0 mt-1" />
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-2">
                    <h6 className="font-medium text-red-900">2019 Honda Civic EX</h6>
                    <span className="text-xs text-red-600 bg-red-100 px-2 py-1 rounded-full">
                      HOT DEAL
                    </span>
                  </div>
                  <p className="text-sm text-red-800 mb-3">
                    Current bid: $18,500 • Market value: $22,000 • Potential savings: $3,500
                  </p>
                  <div className="flex items-center space-x-4 text-xs text-red-700">
                    <span>Ends in: 2h 15m</span>
                    <span>Location: Copart Lot #123</span>
                    <span>Condition: Minor Damage</span>
                  </div>
                  <div className="flex space-x-2 mt-3">
                    <button className="btn-primary text-xs px-3 py-1">Bid Now</button>
                    <button className="btn-secondary text-xs px-3 py-1">View Details</button>
                    <button className="text-gray-500 hover:text-gray-700 text-xs px-3 py-1">
                      Dismiss
                    </button>
                  </div>
                </div>
              </div>

              {/* Sample Alert 2 */}
              <div className="flex items-start space-x-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <EyeIcon className="w-6 h-6 text-yellow-500 flex-shrink-0 mt-1" />
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-2">
                    <h6 className="font-medium text-yellow-900">2020 Toyota Camry LE</h6>
                    <span className="text-xs text-yellow-600 bg-yellow-100 px-2 py-1 rounded-full">
                      WATCH LIST
                    </span>
                  </div>
                  <p className="text-sm text-yellow-800 mb-3">
                    Current bid: $21,200 • Target: $20,500 • Above target by $700
                  </p>
                  <div className="flex items-center space-x-4 text-xs text-yellow-700">
                    <span>Ends in: 4h 30m</span>
                    <span>Location: IAAI Lot #456</span>
                    <span>Condition: Clean</span>
                  </div>
                  <div className="flex space-x-2 mt-3">
                    <button className="btn-secondary text-xs px-3 py-1">Set Max Bid</button>
                    <button className="btn-secondary text-xs px-3 py-1">View Details</button>
                  </div>
                </div>
              </div>

              {/* Sample Alert 3 */}
              <div className="flex items-start space-x-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <ClockIcon className="w-6 h-6 text-blue-500 flex-shrink-0 mt-1" />
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-2">
                    <h6 className="font-medium text-blue-900">2018 Ford F-150 XL</h6>
                    <span className="text-xs text-blue-600 bg-blue-100 px-2 py-1 rounded-full">
                      UPCOMING
                    </span>
                  </div>
                  <p className="text-sm text-blue-800 mb-3">
                    Estimated value: $24,000 • Starts in 6 hours • High demand model
                  </p>
                  <div className="flex items-center space-x-4 text-xs text-blue-700">
                    <span>Starts: Today 2:00 PM</span>
                    <span>Location: Copart Lot #789</span>
                    <span>Condition: Salvage</span>
                  </div>
                  <div className="flex space-x-2 mt-3">
                    <button className="btn-secondary text-xs px-3 py-1">Set Reminder</button>
                    <button className="btn-secondary text-xs px-3 py-1">View Details</button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Auction Performance */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h5 className="text-md font-semibold text-gray-900 mb-4">Auction Performance</h5>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">87%</div>
                <div className="text-xs text-gray-500">Win Rate</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600">$2,340</div>
                <div className="text-xs text-gray-500">Avg Savings</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-purple-600">12</div>
                <div className="text-xs text-gray-500">Vehicles Won</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-orange-600">$28,080</div>
                <div className="text-xs text-gray-500">Total Savings</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Dashboard Settings Modal */}
      {showSettings && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-gray-900">Dashboard Customization</h3>
                <button
                  onClick={() => setShowSettings(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>

              <div className="space-y-6">
                {/* Display Preferences */}
                <div>
                  <h4 className="text-md font-medium text-gray-900 mb-3">Display Preferences</h4>
                  <div className="space-y-3">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={dashboardSettings.showMarketTrends}
                        onChange={(e) =>
                          setDashboardSettings((prev) => ({
                            ...prev,
                            showMarketTrends: e.target.checked,
                          }))
                        }
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">
                        Show market trend indicators
                      </span>
                    </label>

                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={dashboardSettings.showPricePredictions}
                        onChange={(e) =>
                          setDashboardSettings((prev) => ({
                            ...prev,
                            showPricePredictions: e.target.checked,
                          }))
                        }
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">Show price predictions</span>
                    </label>

                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={dashboardSettings.showDemandForecast}
                        onChange={(e) =>
                          setDashboardSettings((prev) => ({
                            ...prev,
                            showDemandForecast: e.target.checked,
                          }))
                        }
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">Show demand forecast data</span>
                    </label>

                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={dashboardSettings.showSeasonalData}
                        onChange={(e) =>
                          setDashboardSettings((prev) => ({
                            ...prev,
                            showSeasonalData: e.target.checked,
                          }))
                        }
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">Show seasonal analytics</span>
                    </label>
                  </div>
                </div>

                {/* Alert Preferences */}
                <div>
                  <h4 className="text-md font-medium text-gray-900 mb-3">Alert Preferences</h4>
                  <div className="space-y-3">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={dashboardSettings.alertPreferences.emailNotifications}
                        onChange={(e) =>
                          setDashboardSettings((prev) => ({
                            ...prev,
                            alertPreferences: {
                              ...prev.alertPreferences,
                              emailNotifications: e.target.checked,
                            },
                          }))
                        }
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">Email notifications</span>
                    </label>

                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={dashboardSettings.alertPreferences.browserNotifications}
                        onChange={(e) =>
                          setDashboardSettings((prev) => ({
                            ...prev,
                            alertPreferences: {
                              ...prev.alertPreferences,
                              browserNotifications: e.target.checked,
                            },
                          }))
                        }
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">Browser notifications</span>
                    </label>

                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={dashboardSettings.alertPreferences.auctionAlerts}
                        onChange={(e) =>
                          setDashboardSettings((prev) => ({
                            ...prev,
                            alertPreferences: {
                              ...prev.alertPreferences,
                              auctionAlerts: e.target.checked,
                            },
                          }))
                        }
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">Auction alerts</span>
                    </label>

                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={dashboardSettings.alertPreferences.priceAlerts}
                        onChange={(e) =>
                          setDashboardSettings((prev) => ({
                            ...prev,
                            alertPreferences: {
                              ...prev.alertPreferences,
                              priceAlerts: e.target.checked,
                            },
                          }))
                        }
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">Price change alerts</span>
                    </label>
                  </div>
                </div>

                {/* Layout Preferences */}
                <div>
                  <h4 className="text-md font-medium text-gray-900 mb-3">Layout Preferences</h4>
                  <div className="space-y-3">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={dashboardSettings.layoutPreferences.compactView}
                        onChange={(e) =>
                          setDashboardSettings((prev) => ({
                            ...prev,
                            layoutPreferences: {
                              ...prev.layoutPreferences,
                              compactView: e.target.checked,
                            },
                          }))
                        }
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">Compact view</span>
                    </label>

                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={dashboardSettings.layoutPreferences.showCharts}
                        onChange={(e) =>
                          setDashboardSettings((prev) => ({
                            ...prev,
                            layoutPreferences: {
                              ...prev.layoutPreferences,
                              showCharts: e.target.checked,
                            },
                          }))
                        }
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">Show charts and graphs</span>
                    </label>

                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={dashboardSettings.layoutPreferences.autoRefresh}
                        onChange={(e) =>
                          setDashboardSettings((prev) => ({
                            ...prev,
                            layoutPreferences: {
                              ...prev.layoutPreferences,
                              autoRefresh: e.target.checked,
                            },
                          }))
                        }
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">Auto-refresh data</span>
                    </label>

                    <div className="flex items-center space-x-2">
                      <span className="text-sm text-gray-700">Refresh interval:</span>
                      <select
                        value={dashboardSettings.layoutPreferences.refreshInterval}
                        onChange={(e) =>
                          setDashboardSettings((prev) => ({
                            ...prev,
                            layoutPreferences: {
                              ...prev.layoutPreferences,
                              refreshInterval: parseInt(e.target.value),
                            },
                          }))
                        }
                        className="text-sm border border-gray-300 rounded px-2 py-1"
                      >
                        <option value={60}>1 minute</option>
                        <option value={300}>5 minutes</option>
                        <option value={600}>10 minutes</option>
                        <option value={1800}>30 minutes</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex justify-end space-x-3 mt-6 pt-4 border-t">
                <button onClick={() => setShowSettings(false)} className="btn-secondary">
                  Cancel
                </button>
                <button
                  onClick={() => {
                    // Save settings (in a real app, this would persist to backend)
                    toast.success('Dashboard settings saved!');
                    setShowSettings(false);
                  }}
                  className="btn-primary"
                >
                  Save Settings
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Export Modal */}
      <ExportStockingGuideModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        dateRange={dateRange}
      />
    </div>
  );
};

export default StockingGuideDashboard;
